import React, { useEffect, useState } from "react";
import { Button, Col, Form, Row, Upload, message } from "antd";
import { CloseOutlined, InboxOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
    apiDeleteMediaResponse,
    apiUploadUserVerificationHistoryMedia,
} from "../../../api/restful/verification";
import useShared from "../../../_common/_shared";
import { getFullUrl } from "."
import { useRecoilValue, useSetRecoilState } from "recoil";
import { globalNotificationState } from "../../../_common/global_state";
import { verificationRefetchState } from "../state";

// single file only
export const FileUploadField = ({
    form,
    field,
    fieldPath, // array of strings ["objectid","objectid"]
    sharedInputProps,
    handleAutoSave
}) => {
    const { t } = useTranslation();

    const setNotificationMessage = useSetRecoilState(globalNotificationState);

    const { readOnly } = sharedInputProps;

    const fileUrl = Form.useWatch(fieldPath, form);
    const [fileList, setFileList] = useState([]);

    useEffect(() => {
        if (!fileUrl) {
            setFileList([]);
            return;
        }

        const verificationURL = localStorage.getItem("verificationURL") || "";

        if (typeof fileUrl === "string") {
            const fullUrl = getFullUrl(verificationURL, fileUrl);

            setFileList([
                {
                    uid: "-1",
                    name: fullUrl.split("/").pop(),
                    status: "done",
                    url: fullUrl,
                    response: fullUrl,
                },
            ]);
        } else if (Array.isArray(fileUrl)) {
            setFileList(
                fileUrl.map((item) => {
                    const itemUrl =
                        item.url || item.response || item.path || "";
                    return {
                        ...item,
                        url: getFullUrl(verificationURL, itemUrl),
                    };
                })
            );
        }
    }, [fileUrl]);

    const handleRemove = async (file) => {
        const currentFiles = form.getFieldValue(fieldPath);
        const currentUrl =
            Array.isArray(currentFiles) && currentFiles.length > 0
                ? currentFiles[0]?.url || currentFiles[0]?.response
                : typeof currentFiles === "string"
                    ? currentFiles
                    : null;

        if (!currentUrl) return;

        try {
            const fileName = currentUrl.split("/").pop();
            await apiDeleteMediaResponse(fileName);
            handleAutoSave?.();
        } catch (error) {
            console.error("failed to remove file: ", error);
        } finally {
            form.setFieldValue(fieldPath, null);
            setFileList([]);
        }
    };

    if (fileUrl) {
        const verificationURL = localStorage.getItem("verificationURL") || "";
        const fullUrl = getFullUrl(verificationURL, fileUrl);

        return (
            <FilePreview
                link={fullUrl}
                isClickable
                isDraft={readOnly}
                handleDelete={handleRemove}
            />
        );
    }

    return (
        <>
            <Upload.Dragger
                name={field.name}
                maxCount={1}
                accept={
                    ".jpg, .jpeg, .png, .webp, .gif, .pdf, .xlsx, .xls, .mp4, .wmv, .avi, .webm"
                }
                fileList={fileList}
                onChange={async ({ file, fileList: newList }) => {
                    setFileList(newList);

                    if (file.status === "done") {
                        const url = file.response || file.url || null;
                        form.setFieldValue(fieldPath, url);

                        // Force AntD to revalidate & clear the error immediately
                        await form.validateFields([fieldPath]).catch(() => { });
                        form.setFields([
                            { name: fieldPath, errors: [] }, // explicitly clear error
                        ]);

                        handleAutoSave?.();
                    }
                    else if (file.status === "removed") {
                        form.setFieldValue(fieldPath, null);
                        await form.validateFields([fieldPath]).catch(() => { });
                        handleAutoSave?.();
                    }
                }}
                onRemove={handleRemove}
                beforeUpload={(file) => {
                    const isLt10M = file.size / 1024 / 1024 < 10;
                    if (!isLt10M) {
                        message.error("File must be smaller than 10MB!");
                        return Upload.LIST_IGNORE;
                    }
                    return true;
                }}
                customRequest={async ({ file, onSuccess, onError }) => {
                    try {
                        const formData = new FormData();
                        formData.append("file", file);

                        const res = await apiUploadUserVerificationHistoryMedia(formData);

                        if (res?.status === "Success") {
                            onSuccess(res.data);
                            message.success(t("accountVerification.uploadSuccess"))
                        } else {
                            message.error(t("accountVerification.uploadFail"));
                            onError?.();
                        }
                    } catch (err) {
                        console.error(err);
                        message.error(t("accountVerification.uploadFail"));
                        onError?.(err);
                    }
                }}
                disabled={readOnly}
                {...sharedInputProps}
            >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                    {t("accountVerification.placeholder.upload")}
                </p>
            </Upload.Dragger>
        </>
    );
};


function FilePreview({ link, isClickable, isDraft, handleDelete }) {
    const { IconCommon } = useShared();
    const result = getLastPartOfUrl(link);
    if (!result) return null;

    const { name, category } = result;

    // --- Main preview content ---
    let content = null;
    switch (category) {
        case "image":
            content = (
                <img
                    src={link}
                    alt={name}
                    style={{
                        width: "auto",
                        maxWidth: "250px",
                        maxHeight: "250px",
                        objectFit: "contain",
                        borderRadius: "8px",
                    }}
                />
            );
            break;
        case "video":
            content = (
                <video
                    src={link}
                    controls
                    style={{
                        width: "100%",
                        maxWidth: "400px",
                        borderRadius: "8px",
                    }}
                />
            );
            break;
        case "document": {
            const specifyType = name.endsWith(".pdf")
                ? "pdf"
                : name.endsWith(".xls") || name.endsWith(".xlsx")
                    ? "xls"
                    : "task";
            content = (
                <Row
                    gutter={10}
                    align="middle"
                    style={{
                        color: "white",
                        fontSize: 16,
                        justifyContent: "center",
                    }}
                >
                    <Col>
                        <IconCommon iconName={specifyType} className="upload-icon" />
                    </Col>
                    <Col flex="auto">{name}</Col>
                </Row>
            );
            break;
        }
        default:
            content = <p style={{ color: "white" }}>{name}</p>;
    }

    // --- Wrapper box ---
    const box = (
        <div
            style={{
                position: "relative",
                backgroundColor: "#1f1f1f",
                borderRadius: "8px",
                padding: "32px",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                width: "100%",
                border: "1px solid #303030",
            }}
        >
            {content}

            {handleDelete && !isDraft && (
                <Button
                    type="text"
                    icon={<CloseOutlined style={{ color: "#aaa" }} />}
                    onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleDelete?.()
                    }}
                    style={{
                        position: "absolute",
                        top: "8px",
                        right: "8px",
                        color: "#aaa",
                    }}
                />
            )}
        </div>
    );

    // --- Make clickable if needed ---
    if (isClickable) {
        return (
            <a
                href={link}
                target="_blank"
                rel="noopener noreferrer"
                style={{ display: "block", width: "100%" }}
            >
                {box}
            </a>
        );
    }

    return box;
}

function getLastPartOfUrl(url) {
    const IMAGE_EXT = [".jpg", ".jpeg", ".png", ".webp"];
    const VIDEO_EXT = [".mp4", ".wmv", ".avi", ".webm"];
    const DOCUMENT_EXT = [".pdf", ".xlsx", ".xls"];

    if (!url) {
        return null;
    }
    // Split the URL by slashes "/"
    const parts = url.split("/");

    // Get the last part of the URL
    const name = parts[parts.length - 1];

    const category = IMAGE_EXT.some((d) => name?.includes(d))
        ? "image"
        : VIDEO_EXT.some((d) => name?.includes(d))
            ? "video"
            : DOCUMENT_EXT.some((d) => name?.includes(d))
                ? "document"
                : null;

    const formats = name.split(".");
    const format = formats[formats.length - 1];

    return { name, format, category };
}
